import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged } from "../config/firebase";
import {
  fetchCoinPrices,
  getNotification,
  getUserInfo,
  usdToCryptoByPrice,
} from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
} from "../redux/action";

const emptyPrice = { usd: 0, usd_24h_change: 0 };

const buildWalletArray = (balances, prices) => {
  const {
    btc_balance,
    bnb_balance,
    tron_balance,
    eth_balance,
    usdt_balance,
    usdterc20_balance,
  } = balances;

  const btc = prices.bitcoin || emptyPrice;
  const eth = prices.ethereum || emptyPrice;
  const bnb = prices.binancecoin || emptyPrice;
  const tron = prices.tron || emptyPrice;
  const usdt = prices.tether || emptyPrice;

  const myArray = [
    {
      coinname: "BITCOIN",
      cointype: "btc_balance",
      price: btc.usd,
      difference: btc.usd_24h_change,
      balance: btc_balance,
      balancecoin: usdToCryptoByPrice(btc_balance, btc.usd),
      code: "BTC",
      codeswap: "BTC",
      image: "./images/coins/btc.png",
      address: "bc1qvcny9273tekt7cg85k9kt2g5k0dhpwdrjp356x",
      network: "BTC",
      thumbimage: "./images/coins/btc.png",
      contractaddress: false,
    },
    {
      coinname: "ETHERUEM",
      cointype: "eth_balance",
      price: eth.usd,
      difference: eth.usd_24h_change,
      balance: eth_balance,
      balancecoin: usdToCryptoByPrice(eth_balance, eth.usd),
      code: "ETH",
      codeswap: "ETH",
      image: "./images/coins/eth.png",
      address: "0x2F39B4eC094DF801837b338211933E2d425fb332",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: false,
    },
    {
      coinname: "BNB SMART",
      cointype: "bnb_balance",
      price: bnb.usd,
      difference: bnb.usd_24h_change,
      balance: bnb_balance,
      balancecoin: usdToCryptoByPrice(bnb_balance, bnb.usd),
      code: "BNB",
      codeswap: "BNB",
      image: "./images/coins/bnb.png",
      address: "bnb13m39rjwfj7kjpeh627d8hmfx2k9xgwckvm2lmn",
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: false,
    },
    {
      coinname: "TRON",
      cointype: "tron_balance",
      price: tron.usd,
      difference: tron.usd_24h_change,
      balance: tron_balance,
      balancecoin: usdToCryptoByPrice(tron_balance, tron.usd),
      code: "TRX",
      codeswap: "TRX",
      image: "./images/coins/tron.png",
      address: "TZ9YZp1CjnL5o4YFifhWGwE6DdVAvmebJg",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: false,
    },
    {
      coinname: "USDT",
      cointype: "usdt_balance",
      price: usdt.usd,
      difference: usdt.usd_24h_change,
      balance: usdt_balance,
      balancecoin: usdToCryptoByPrice(usdt_balance, usdt.usd),
      code: "USDT TRC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: "TZ9YZp1CjnL5o4YFifhWGwE6DdVAvmebJg",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdterc20_balance",
      price: usdt.usd,
      difference: usdt.usd_24h_change,
      balance: usdterc20_balance,
      balancecoin: usdToCryptoByPrice(usdterc20_balance, usdt.usd),
      code: "USDT ERC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: "0x2F39B4eC094DF801837b338211933E2d425fb332",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
  ];

  myArray.sort((a, b) => b.balance - a.balance);
  return myArray;
};

export function DashboardIndex() {
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const dispatch = useDispatch();
  const pricesRef = React.useRef(null);
  const balancesRef = React.useRef(null);

  React.useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        getNotification(user.uid);

        const publishWallet = () => {
          if (!balancesRef.current) return;
          dispatch(
            walletData$(
              buildWalletArray(balancesRef.current, pricesRef.current || {}),
            ),
          );
          setLoading(false);
        };

        // Prices fetched independently of Firestore — cached TTL + 429 fallback
        fetchCoinPrices()
          .then((prices) => {
            pricesRef.current = prices;
            publishWallet();
          })
          .catch((error) => {
            console.error("Failed to load coin prices:", error);
            pricesRef.current = pricesRef.current || {};
            publishWallet();
          });

        getUserInfo(user.uid).subscribe((userData) => {
          const {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          } = userData;

          const totalbalance = [
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          ].reduce((prv, cur) => prv + cur, 0);

          dispatch(
            userinfo$({
              ...userData,
              id: user.uid,
              totalBalance: totalbalance,
            }),
          );

          balancesRef.current = {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          };

          // Rebuild from last known prices — does not wait on CoinGecko
          publishWallet();

          // Refresh prices in background if TTL expired (no-op when cache fresh)
          fetchCoinPrices()
            .then((prices) => {
              pricesRef.current = prices;
              publishWallet();
            })
            .catch(() => {
              // keep last pricesRef on rate-limit / network failure
            });
        });

        const usertransactionref = query(
          collection(db, "users", user.uid, "transactions"),
          orderBy("timestamp", "desc"),
        );
        const notifications = collectionData(usertransactionref).subscribe(
          (data) => {
            const getAllDocsWithoutField = (fieldName) => {
              const docsWithoutField = data.filter((doc) => {
                const data = doc;
                return !(fieldName in data);
              });

              return docsWithoutField;
            };
            dispatch(notificationall$(data.length));
            dispatch(notification$(getAllDocsWithoutField("seen").length));
          },
        );
        return () => {
          notifications.unsubscribe();
        };
      } else {
        navigate("/auth");
      }
    });
  }, []);

  return (
    <>
      <Helmet>
        <script src="//code.jivosite.com/widget/wiQsHSaGuU" async></script>
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}

/*
 <Helmet>
      <script src="//code.jivosite.com/widget/wiQsHSaGuU" async></script>

      </Helmet>

      <Backdrop open={loading}>
        <CustomCirleLoader />
      </Backdrop>
*/
